import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { AddProductEndPoint } from '../../service/global.service';
import { HttpHelpService } from '../../service/http-help.service';
import { AddMerchantCategoryModalComponent } from '../../../components/shared/add-merchant-category-modal/add-merchant-category-modal.component';
import { companyDisplayName } from '../../../shared/utils/company-display-name';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
})
export class ProductFormComponent implements OnInit {
  @ViewChild('aiPreviewModal') aiPreviewModal: any;

  curentLang = localStorage.getItem('curentLang') || 'ar';
  userRegistId = JSON.parse(localStorage.getItem('user_id'));
  merchantId = JSON.parse(localStorage.getItem('merchant_id'));
  user_type = JSON.parse(localStorage.getItem('user_type'));

  isEditMode = false;
  productId: number | null = null;
  validate = false;

  productForm: FormGroup;
  categoriesData: any[] = [];
  unitMesauereData: any[] = [];
  companiesData: any[] = [];
  merchantCategoriesData: any[] = [];

  currentImageUrl: string | null = null;
  aiPreviewUrl: string | null = null;
  aiPreviewKey: string | null = null;
  aiPrompt = '';
  pendingPreviewKey: string | null = null;
  loadedProductStatus: number | null = null;
  readonly addCategoryOptionValue = -1;

  private lastCategoryId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private service: HttpHelpService,
    private modalService: NgbModal,
  ) {
    this.productForm = this.buildFormGroup();
  }

  private buildFormGroup(): FormGroup {
    return new FormGroup({
      product_name_en: new FormControl(''),
      product_name_ar: new FormControl(''),
      product_name_he: new FormControl('', Validators.required),
      des_ar: new FormControl(''),
      des_en: new FormControl(''),
      des_he: new FormControl('', Validators.required),
      product_code: new FormControl(''),
      barcode: new FormControl(''),
      company_id: new FormControl(null, Validators.required),
      price: new FormControl(null, Validators.required),
      measurement_unit_id: new FormControl(null, Validators.required),
      categories_id: new FormControl(null),
      merchant_category_id: new FormControl(null),
      show_in_pos: new FormControl(true),
      is_product_active: new FormControl(true),
      pos_sort_order: new FormControl(0),
      generate_ai_image: new FormControl(false),
      ai_image_prompt: new FormControl(''),
      merchant_id: new FormControl(this.merchantId),
      created_by: new FormControl(this.userRegistId),
      updated_by: new FormControl(this.userRegistId),
    });
  }

  ngOnInit(): void {
    this.loadDropdowns();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.productId = Number(idParam);
      this.loadProduct(this.productId);
    } else {
      this.resetForm();
    }
  }

  private msg(ar: string, en: string, he: string): string {
    return this.curentLang === 'ar' ? ar : this.curentLang === 'en' ? en : he;
  }

  private apiMsg(res: any): string {
    return this.curentLang === 'ar'
      ? res.messageAr
      : this.curentLang === 'en'
        ? res.messageEn
        : res.messageHe;
  }

  pageTitle(): string {
    if (this.isEditMode) {
      return this.msg('تعديل منتج', 'Edit Product', 'עריכת מוצר');
    }
    return this.msg('منتج جديد', 'New Product', 'מוצר חדש');
  }

  saveButtonLabel(): string {
    if (this.isEditMode) {
      return this.msg('حفظ التعديلات', 'Save Changes', 'שמור שינויים');
    }
    return this.msg('إضافة منتج', 'Add Product', 'הוספת מוצר');
  }

  getProductTitle(): string {
    return String(this.productForm.value.product_name_he || '').trim();
  }

  canGenerateAiImage(): boolean {
    return this.getProductTitle().length > 0;
  }

  aiImageHint(): string {
    return this.msg(
      'أدخل اسم المنتج أولاً. كلما وصف الاسم المنتج بدقة، كانت صورة الذكاء الاصطناعي أدق.',
      'Enter the product name first. The more accurately the title describes the product, the better the AI photo will be.',
      'הזן תחילה את שם המוצר. ככל שהשם מתאר את המוצר בדיוק רב יותר, תמונת ה-AI תהיה מדויקת יותר.',
    );
  }

  addCategoryLabel(): string {
    return this.msg('إضافة تصنيف', 'Add category', 'הוסף קטגוריה');
  }

  onMerchantCategoryChange(value: number | null): void {
    if (value === this.addCategoryOptionValue) {
      const revert =
        this.lastCategoryId ??
        (this.merchantCategoriesData[0]
          ? this.merchantCategoryPk(this.merchantCategoriesData[0])
          : null);
      setTimeout(() => {
        this.productForm.patchValue({ merchant_category_id: revert }, { emitEvent: false });
      });
      this.openAddCategoryModal();
      return;
    }
    if (value != null) {
      this.lastCategoryId = value;
    }
  }

  openAddCategoryModal(): void {
    const ref = this.modalService.open(AddMerchantCategoryModalComponent, {
      size: 'xl',
      centered: true,
    });
    ref.result.then(
      (created) => {
        const newId = this.merchantCategoryPk(created);
        this.reloadMerchantCategories(newId || undefined);
      },
      () => undefined,
    );
  }

  private reloadMerchantCategories(selectId?: number): void {
    this.service.getActiveMerchantProductCategories().subscribe({
      next: (res) => {
        this.merchantCategoriesData = Array.isArray(res)
          ? res
          : res?.data || res?.categories || [];

        let idToSelect = selectId;
        if (idToSelect && !this.merchantCategoriesData.some((c) => this.merchantCategoryPk(c) === idToSelect)) {
          idToSelect = undefined;
        }
        if (!idToSelect && this.merchantCategoriesData.length) {
          idToSelect = this.merchantCategoryPk(
            this.merchantCategoriesData[this.merchantCategoriesData.length - 1],
          );
        }
        if (idToSelect) {
          this.productForm.patchValue({ merchant_category_id: idToSelect });
          this.lastCategoryId = idToSelect;
        }
      },
      error: () => {
        this.merchantCategoriesData = [];
      },
    });
  }

  private applyDefaultMeasurementUnit(): void {
    if (this.isEditMode) return;
    const current = this.productForm.value.measurement_unit_id;
    if (current) return;
    if (!this.unitMesauereData?.length) return;
    this.productForm.patchValue({
      measurement_unit_id: Number(this.unitMesauereData[0].measurement_unit_id),
    });
  }

  private applyDefaultCategory(): void {
    if (this.isEditMode) return;
    const current = this.productForm.value.merchant_category_id;
    if (current != null && current !== this.addCategoryOptionValue) {
      this.lastCategoryId = current;
      return;
    }
    if (!this.merchantCategoriesData?.length) return;
    const firstId = this.merchantCategoryPk(this.merchantCategoriesData[0]);
    this.productForm.patchValue({ merchant_category_id: firstId });
    this.lastCategoryId = firstId;
  }

  private applyDefaultGeneralCategory(): void {
    if (this.isEditMode) return;
    const current = this.productForm.value.categories_id;
    if (current != null) return;
    if (!this.categoriesData?.length) return;
    const firstId = Number(this.categoriesData[0]?.categories_id);
    if (!firstId) return;
    this.productForm.patchValue({ categories_id: firstId });
  }

  private applyDefaultCompany(): void {
    if (this.isEditMode) return;
    if (this.productForm.value.company_id) return;
    if (this.companiesData.length !== 1) return;

    const companyId = Number(this.companiesData[0]?.company_id);
    if (!companyId) return;

    this.productForm.patchValue({ company_id: companyId });
  }

  merchantCategoryPk(item: any): number {
    return Number(item?.merchant_category_id ?? item?.id);
  }

  merchantCategoryLabel(item: any): string {
    if (!item) return '';
    return this.curentLang === 'en'
      ? item.name_en
      : this.curentLang === 'ar'
        ? item.name_ar
        : item.name_he;
  }

  selectedMerchantCategoryImageUrl(): string | null {
    const id = this.productForm?.value?.merchant_category_id;
    if (id == null || id === this.addCategoryOptionValue) return null;
    const item = this.merchantCategoriesData.find(
      (c) => this.merchantCategoryPk(c) === Number(id),
    );
    const url = item?.image_url;
    return url ? String(url) : null;
  }

  companyLabel(item: any): string {
    return companyDisplayName(item, this.curentLang);
  }

  measurementUnitLabel(item: any): string {
    return this.curentLang === 'en'
      ? item?.measurement_unit_name_en
      : this.curentLang === 'ar'
        ? item?.measurement_unit_name_ar
        : item?.measurement_unit_name_he;
  }

  generalCategoryLabel(item: any): string {
    if (!item) return '';
    return this.curentLang === 'en'
      ? item.categories_name_en
      : this.curentLang === 'ar'
        ? item.categories_name_ar
        : item.categories_name_he;
  }

  private buildPosFields(form: FormGroup) {
    const companyId = form.value.company_id ? Number(form.value.company_id) : null;
    const productCode = String(form.value.product_code || '').trim();
    const barcode = String(form.value.barcode || '').trim();
    const rawCategoryId = form.value.merchant_category_id;
    const merchantCategoryId =
      rawCategoryId != null && Number(rawCategoryId) > 0 ? Number(rawCategoryId) : null;
    return {
      product_code: productCode || undefined,
      barcode: barcode || undefined,
      company_id: companyId || undefined,
      merchant_category_id: merchantCategoryId || undefined,
      show_in_pos: form.value.show_in_pos !== false,
      pos_sort_order: Number(form.value.pos_sort_order) || 0,
      auto_generate_sku: null,
      auto_generate_barcode: null,
      generate_ai_image: !!form.value.generate_ai_image,
      ai_image_prompt: String(form.value.ai_image_prompt || '').trim() || undefined,
    };
  }

  private buildImageSaveFields(form: FormGroup) {
    const fields: any = {};
    if (this.pendingPreviewKey) {
      fields.preview_key = this.pendingPreviewKey;
    } else if (form.value.generate_ai_image) {
      fields.generate_ai_image = true;
      const prompt = String(form.value.ai_image_prompt || this.aiPrompt || '').trim();
      if (prompt) fields.ai_image_prompt = prompt;
    }
    return fields;
  }

  private toPlainText(value: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = value || '';
    return (tmp.textContent || tmp.innerText || '').trim();
  }

  resetForm(): void {
    this.productForm.reset({
      product_name_en: '',
      product_name_ar: '',
      product_name_he: '',
      des_ar: '',
      des_en: '',
      des_he: '',
      product_code: '',
      barcode: '',
      company_id: null,
      price: null,
      measurement_unit_id: null,
      categories_id: null,
      merchant_category_id: null,
      show_in_pos: true,
      is_product_active: true,
      pos_sort_order: 0,
      generate_ai_image: false,
      ai_image_prompt: '',
      merchant_id: this.merchantId,
      created_by: this.userRegistId,
      updated_by: this.userRegistId,
    });
    this.pendingPreviewKey = null;
    this.aiPrompt = '';
    this.currentImageUrl = null;
    this.validate = false;
    this.applyDefaultMeasurementUnit();
    this.applyDefaultCategory();
    this.applyDefaultGeneralCategory();
    this.applyDefaultCompany();
  }

  loadDropdowns(): void {
    this.service.getAllActiveCategories().subscribe({
      next: (res) => {
        this.categoriesData = res;
        this.applyDefaultGeneralCategory();
      },
    });
    this.service.getAllActiveMeasurementUnits().subscribe({
      next: (res) => {
        this.unitMesauereData = res;
        this.applyDefaultMeasurementUnit();
      },
    });
    this.service.getAllActiveCompanyByMerchant().subscribe({
      next: (res) => {
        this.companiesData = Array.isArray(res) ? res : res?.data || [];
        this.applyDefaultCompany();
      },
      error: () => (this.companiesData = []),
    });
    this.service.getActiveMerchantProductCategories().subscribe({
      next: (res) => {
        this.merchantCategoriesData = Array.isArray(res)
          ? res
          : res?.data || res?.categories || [];
        this.applyDefaultCategory();
      },
      error: () => (this.merchantCategoriesData = []),
    });
  }

  loadProduct(id: number): void {
    this.spinner.show();
    this.service.GetProductbycode(id).subscribe({
      next: (data: any) => {
        this.spinner.hide();
        if (!data?.product_id) {
          Swal.fire({
            icon: 'error',
            title: this.msg('المنتج غير موجود', 'Product not found', 'המוצר לא נמצא'),
          });
          this.router.navigate(['/dashboard/products']);
          return;
        }

        this.productForm.patchValue({
          product_name_en: data.product_name_en || '',
          product_name_ar: data.product_name_ar || '',
          product_name_he: data.product_name_he || '',
          des_ar: this.toPlainText(data.des_ar || ''),
          des_en: this.toPlainText(data.des_en || ''),
          des_he: this.toPlainText(data.des_he || ''),
          product_code: data.product_code || '',
          barcode: data.barcode || '',
          company_id: data.company_id || null,
          price: data.price,
          measurement_unit_id: data.measurement_unit_id || data.measurementUnits?.measurement_unit_id || null,
          categories_id: data.categories_id || data.categories?.categories_id || null,
          merchant_category_id: data.merchant_category_id || null,
          show_in_pos: data.show_in_pos !== false && data.show_in_pos !== 0,
          is_product_active: (data.product_status ?? 1) === 1,
          pos_sort_order: data.pos_sort_order ?? 0,
          generate_ai_image: false,
          ai_image_prompt: '',
          merchant_id: this.merchantId,
          updated_by: this.userRegistId,
        });
        if (data.merchant_category_id) {
          this.lastCategoryId = Number(data.merchant_category_id);
        }
        this.currentImageUrl = data.image_url || null;
        this.pendingPreviewKey = null;
        this.loadedProductStatus = data.product_status ?? 1;
      },
      error: () => {
        this.spinner.hide();
        Swal.fire({
          icon: 'error',
          title: this.msg('فشل تحميل المنتج', 'Failed to load product', 'טעינת המוצר נכשלה'),
        });
        this.router.navigate(['/dashboard/products']);
      },
    });
  }

  save(): void {
    const productNameHe = String(this.productForm.value.product_name_he || '').trim();
    const price = Number(this.productForm.value.price);
    const desHe = String(this.productForm.value.des_he || '').trim();
    const measurementUnitId = Number(this.productForm.value.measurement_unit_id) || 0;
    const companyId = Number(this.productForm.value.company_id) || 0;

    if (!productNameHe || !price || !desHe || !measurementUnitId || !companyId) {
      Swal.fire({
        icon: 'warning',
        title: this.msg(
          'يرجى ملء الحقول المطلوبة',
          'Please fill out the required fields',
          'אנא מלא את השדות הנדרשים',
        ),
      });
      this.productForm.markAllAsTouched();
      this.validate = !this.validate;
      return;
    }

    this.productForm.patchValue({
      product_name_ar: productNameHe,
      product_name_en: productNameHe,
      des_ar: this.productForm.value.des_he,
      des_en: this.productForm.value.des_he,
    });

    const pos = this.buildPosFields(this.productForm);
    const imageFields = this.buildImageSaveFields(this.productForm);

    const model: any = {
      product_name_en: this.productForm.value.product_name_en,
      product_name_ar: this.productForm.value.product_name_ar,
      product_name_he: this.productForm.value.product_name_he,
      product_code: pos.product_code || '',
      barcode: pos.barcode,
      company_id: pos.company_id,
      price,
      des_ar: this.productForm.value.des_ar || '',
      des_en: this.productForm.value.des_en || '',
      des_he: this.productForm.value.des_he || '',
      measurement_unit_id: measurementUnitId,
      categories_id: this.productForm.value.categories_id || null,
      merchant_category_id: pos.merchant_category_id,
      show_in_pos: pos.show_in_pos,
      pos_sort_order: pos.pos_sort_order,
      auto_generate_sku: pos.auto_generate_sku,
      auto_generate_barcode: pos.auto_generate_barcode,
      quantity_limited: false,
      has_options: false,
      ...imageFields,
      merchant_id: Number(this.merchantId),
    };

    this.spinner.show();

    if (this.isEditMode && this.productId) {
      model.updated_by = Number(this.userRegistId);
      this.service.updateProductsById(this.productId, model).subscribe({
        next: (res) => this.handleSaveResponse(res),
        error: () => {
          this.spinner.hide();
          Swal.fire({
            icon: 'error',
            title: this.msg('فشل حفظ المنتج', 'Failed to save product', 'שמירת המוצר נכשלה'),
          });
        },
      });
    } else {
      model.created_by = Number(this.userRegistId);
      this.service.Post(AddProductEndPoint.POST, model).subscribe({
        next: (res) => this.handleSaveResponse(res),
        error: () => {
          this.spinner.hide();
          Swal.fire({
            icon: 'error',
            title: this.msg('فشل إضافة المنتج', 'Failed to add product', 'הוספת המוצר נכשלה'),
          });
        },
      });
    }
  }

  private handleSaveResponse(res: any): void {
    this.spinner.hide();
    if (res.status === 200) {
      const savedId = res.data?.product_id || res.product_id || this.productId;
      const desiredStatus = this.productForm.value.is_product_active ? 1 : 59;
      const needsStatusUpdate =
        savedId &&
        (this.isEditMode
          ? this.loadedProductStatus != null && desiredStatus !== this.loadedProductStatus
          : desiredStatus === 59);

      const finish = () => {
        Swal.fire({
          icon: 'success',
          title: this.apiMsg(res),
        }).then(() => this.router.navigate(['/dashboard/products']));
      };

      if (needsStatusUpdate) {
        this.service
          .changeProductStatus(savedId, {
            product_status: desiredStatus === 1 ? 1 : 59,
            updated_by: this.userRegistId,
          })
          .subscribe({ next: () => finish(), error: () => finish() });
      } else {
        finish();
      }
    } else if (res.status === 400 || res.status === 403 || res.status === 500) {
      Swal.fire({
        icon: 'error',
        title: this.apiMsg(res),
      });
    }
  }

  triggerImageUpload(input: HTMLInputElement): void {
    input.click();
  }

  onProductImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.productId) {
      Swal.fire({
        icon: 'info',
        title: this.msg(
          'احفظ المنتج أولاً ثم ارفع الصورة',
          'Save the product first, then upload the image',
          'שמור את המוצר תחילה ואז העלה את התמונה',
        ),
      });
      input.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    this.spinner.show();
    this.service.uploadProductImage(this.productId, formData).subscribe({
      next: (res) => {
        this.spinner.hide();
        input.value = '';
        if (res.status === 200 || res.image_url || res.data?.image_url) {
          const updated = res.data || res;
          this.currentImageUrl = updated.image_url || res.image_url || this.currentImageUrl;
          Swal.fire({
            icon: 'success',
            title: this.apiMsg(res) || this.msg('تم رفع الصورة', 'Image uploaded', 'התמונה הועלתה'),
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: this.apiMsg(res) || this.msg('فشل رفع الصورة', 'Image upload failed', 'העלאת התמונה נכשלה'),
          });
        }
      },
      error: () => {
        this.spinner.hide();
        input.value = '';
        Swal.fire({
          icon: 'error',
          title: this.msg('فشل رفع الصورة', 'Image upload failed', 'העלאת התמונה נכשלה'),
        });
      },
    });
  }

  generateAiImage(): void {
    const productName = this.getProductTitle();

    if (!productName) {
      Swal.fire({
        icon: 'warning',
        title: this.msg(
          'يرجى إدخال اسم المنتج قبل توليد الصورة',
          'Please enter the product name before generating an image',
          'יש להזין את שם המוצר לפני יצירת תמונה',
        ),
        text: this.aiImageHint(),
      });
      this.productForm.get('product_name_he')?.markAsTouched();
      return;
    }

    const prompt =
      String(this.productForm.value.ai_image_prompt || this.aiPrompt || '').trim() || undefined;

    this.spinner.show();
    this.service
      .aiGenerateProductImage({
        merchant_id: Number(this.merchantId),
        prompt,
        product_name: productName,
      })
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          const data = res.data || res;
          this.aiPreviewUrl = data.preview_url || null;
          this.aiPreviewKey = data.preview_key || null;
          if (!this.aiPreviewUrl || !this.aiPreviewKey) {
            Swal.fire({
              icon: 'error',
              title:
                this.apiMsg(res) ||
                this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
            });
            return;
          }
          this.modalService.open(this.aiPreviewModal, { size: 'lg', centered: true });
        },
        error: () => {
          this.spinner.hide();
          Swal.fire({
            icon: 'error',
            title: this.msg('فشل توليد الصورة', 'AI image generation failed', 'יצירת התמונה נכשלה'),
          });
        },
      });
  }

  approveAiImage(modal?: any): void {
    if (!this.aiPreviewKey) return;

    if (!this.productId) {
      this.pendingPreviewKey = this.aiPreviewKey;
      this.currentImageUrl = this.aiPreviewUrl;
      this.productForm.patchValue({ generate_ai_image: false });
      this.aiPreviewUrl = null;
      this.aiPreviewKey = null;
      if (modal) modal.close();
      Swal.fire({
        icon: 'success',
        title: this.msg(
          'تم تجهيز الصورة — احفظ المنتج لاعتمادها',
          'Image ready — save the product to apply it',
          'התמונה מוכנה — שמור את המוצר כדי להחיל אותה',
        ),
      });
      return;
    }

    this.spinner.show();
    this.service
      .approveAiProductImage(this.productId, { preview_key: this.aiPreviewKey })
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          if (res.status === 200 || res.image_url || res.data?.image_url) {
            const updated = res.data || res;
            this.currentImageUrl = updated.image_url || res.image_url || this.currentImageUrl;
            this.pendingPreviewKey = null;
            this.aiPreviewUrl = null;
            this.aiPreviewKey = null;
            if (modal) modal.close();
            Swal.fire({
              icon: 'success',
              title: this.apiMsg(res) || this.msg('تم اعتماد الصورة', 'Image approved', 'התמונה אושרה'),
            });
          } else {
            this.pendingPreviewKey = this.aiPreviewKey;
            this.currentImageUrl = this.aiPreviewUrl;
            if (modal) modal.close();
            Swal.fire({
              icon: 'info',
              title: this.msg(
                'احفظ التعديل لاعتماد الصورة',
                'Save the update to apply the image',
                'שמור את העריכה כדי להחיל את התמונה',
              ),
            });
          }
        },
        error: () => {
          this.spinner.hide();
          this.pendingPreviewKey = this.aiPreviewKey;
          this.currentImageUrl = this.aiPreviewUrl;
          if (modal) modal.close();
          Swal.fire({
            icon: 'info',
            title: this.msg(
              'احفظ التعديل لاعتماد الصورة',
              'Save the update to apply the image',
              'שמור את העריכה כדי להחיל את התמונה',
            ),
          });
        },
      });
  }
}
